import { pcm16ToWavBlob } from './wav-encode';
import { getEmbedApiContext } from '../api/context';

/**
 * Discovery + synthesis client for a self-hosted VoxCPM2 server,
 * routed through admin-api's character-keyed proxy. The browser passes
 * a `characterId`; admin-api looks up `voice.voxcpm.baseUrl` from
 * Cosmos and forwards. The browser NEVER sees the upstream URL.
 *
 * See `secret-isolation` capability spec for the rationale.
 */

/**
 * `/voices/all` returns `styles` as a flat string[] (just the style
 * names). The richer per-style metadata (filename, ref_text) lives at
 * a separate per-voice endpoint we don't currently call.
 */
export interface VoxCpmVoiceEntry {
  description?: string;
  styles: string[];
}

export type VoxCpmVoicesAll = Record<string, VoxCpmVoiceEntry>;

/**
 * Lists every voice + style available on the character's configured
 * VoxCPM2 upstream. Hits `GET /api/tts-voxcpm/{characterId}/voices-all`;
 * admin-api resolves the upstream URL from Cosmos.
 */
export async function getVoicesAll(characterId: string): Promise<VoxCpmVoicesAll> {
  const ctx = getEmbedApiContext();
  const res = await fetch(ctx.apiUrl(`/api/tts-voxcpm/${encodeURIComponent(characterId)}/voices-all`), {
    method: 'GET',
    headers: ctx.apiAuthHeaders(),
  });
  if (!res.ok) {
    const body = await safeBody(res);
    throw new Error(`VoxCPM /voices/all HTTP ${res.status}: ${body}`);
  }
  const json = (await res.json()) as { voices?: VoxCpmVoicesAll };
  return json.voices ?? {};
}

export interface VoxCpmSynthesizePayload {
  text: string;
  /** Optional override (else server uses persisted character config). */
  voice?: string;
  /** Optional override (else server uses persisted character config). */
  style?: string;
  /** Optional cloning instruction — Controllable Cloning when set. */
  instructText?: string;
  /** Override `max_new_tokens` (server default 2048). */
  maxNewTokens?: number;
  /** Cancel mid-fetch (per `embed-mid-reply-interrupt`). When the
   *  signal aborts, the streaming reader rejects with AbortError;
   *  speak() handles the cleanup. */
  signal?: AbortSignal;
}

/**
 * Fetches PCM16 audio for the supplied text via the character-keyed
 * admin-api proxy. Returns concatenated bytes ready to wrap in a WAV
 * header and play through HTMLAudioElement.
 */
export async function synthesizePcm(
  characterId: string,
  payload: VoxCpmSynthesizePayload,
): Promise<Uint8Array> {
  const ctx = getEmbedApiContext();
  const body: Record<string, unknown> = { text: payload.text };
  if (payload.voice) body.voice = payload.voice;
  if (payload.style) body.style = payload.style;
  if (payload.instructText && payload.instructText.trim()) body.instructText = payload.instructText.trim();
  if (payload.maxNewTokens !== undefined) body.maxNewTokens = payload.maxNewTokens;
  const res = await fetch(ctx.apiUrl(`/api/tts-voxcpm/${encodeURIComponent(characterId)}/synthesize`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...ctx.apiAuthHeaders() },
    body: JSON.stringify(body),
    signal: payload.signal,
  });
  if (!res.ok) {
    const errBody = await safeBody(res);
    throw new Error(`VoxCPM /synthesize HTTP ${res.status}: ${errBody}`);
  }
  const reader = res.body?.getReader();
  if (!reader) {
    return new Uint8Array(await res.arrayBuffer());
  }
  const chunks: Uint8Array[] = [];
  let total = 0;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    if (value && value.byteLength > 0) {
      chunks.push(value);
      total += value.byteLength;
    }
  }
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.byteLength;
  }
  return out;
}

/** Convenience wrapper: synthesise + wrap → ready-to-play WAV blob. */
export async function synthesizeBlob(
  characterId: string,
  payload: VoxCpmSynthesizePayload,
): Promise<Blob> {
  const pcm = await synthesizePcm(characterId, payload);
  return pcm16ToWavBlob(pcm, 48000, 1);
}

async function safeBody(res: Response): Promise<string> {
  try { return (await res.text()).slice(0, 500); }
  catch { return '<unreadable>'; }
}
